import React, { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

// Icons
const ChartBarIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
)

const ClockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const ExclamationTriangleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
)

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const ArrowUpIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
  </svg>
)

const ArrowDownIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
  </svg>
)

// Statistics Card Component
const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => {
  const getTrendColor = (trend) => {
    if (trend === 'up') return 'text-green-600'
    if (trend === 'down') return 'text-red-600'
    return 'text-gray-600'
  }

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUpIcon className="w-4 h-4" />
    if (trend === 'down') return <ArrowDownIcon className="w-4 h-4" />
    return null
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className="flex items-center">
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
            {trend && (
              <div className={`ml-2 flex items-center ${getTrendColor(trend)}`}>
                {getTrendIcon(trend)}
                <span className="text-sm font-medium ml-1">{trendValue}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Chart Component (Simple Bar Chart)
const SimpleBarChart = ({ data, title }) => {
  const maxValue = Math.max(...Object.values(data))
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center">
            <div className="w-20 text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</div>
            <div className="flex-1 mx-4">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full" 
                  style={{ width: `${(value / maxValue) * 100}%` }}
                ></div>
              </div>
            </div>
            <div className="w-12 text-sm text-gray-900 text-right">{value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Main Analytics Dashboard Component
const AnalyticsDashboard = () => {
  const { user } = useAuthStore()
  const [statistics, setStatistics] = useState(null)
  const [escalatedComplaints, setEscalatedComplaints] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    end_date: new Date().toISOString().split('T')[0] // today
  })

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setIsLoading(true)
      try {
        // Fetch statistics
        const statsResponse = await fetch(`/api/v1/complaints/statistics?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json()
          setStatistics(statsData.data)
        }

        // Fetch escalated complaints
        const escalatedResponse = await fetch('/api/v1/complaints/escalated?limit=10', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (escalatedResponse.ok) {
          const escalatedData = await escalatedResponse.json()
          setEscalatedComplaints(escalatedData.data || [])
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalyticsData()
  }, [dateRange])

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const refreshData = () => {
    const fetchAnalyticsData = async () => {
      setIsLoading(true)
      try {
        const statsResponse = await fetch(`/api/v1/complaints/statistics?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json()
          setStatistics(statsData.data)
        }
      } catch (error) {
        console.error('Error refreshing data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalyticsData()
  }

  if (isLoading && !statistics) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    )
  }

  const totalComplaints = statistics ? Object.values(statistics.statusCounts).reduce((sum, count) => sum + count, 0) : 0
  const resolvedComplaints = statistics?.statusCounts?.resolved || 0
  const pendingComplaints = statistics?.statusCounts?.pending || 0
  const inProgressComplaints = statistics?.statusCounts?.in_progress || 0
  const escalatedCount = statistics?.escalationCount || 0

  const resolutionRate = totalComplaints > 0 ? ((resolvedComplaints / totalComplaints) * 100).toFixed(1) : 0
  const avgResolutionHours = statistics?.resolutionStats?.avg_hours || 0

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Comprehensive insights into complaint management performance
              </p>
            </div>
            <div className="flex space-x-4">
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => handleDateRangeChange('start_date', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => handleDateRangeChange('end_date', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Button onClick={refreshData} variant="outline">
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Complaints"
            value={totalComplaints}
            icon={ChartBarIcon}
            color="bg-blue-500"
          />
          <StatCard
            title="Resolution Rate"
            value={`${resolutionRate}%`}
            icon={CheckCircleIcon}
            color="bg-green-500"
          />
          <StatCard
            title="Avg Resolution Time"
            value={`${avgResolutionHours.toFixed(1)}h`}
            icon={ClockIcon}
            color="bg-yellow-500"
          />
          <StatCard
            title="Escalated Complaints"
            value={escalatedCount}
            icon={ExclamationTriangleIcon}
            color="bg-red-500"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {statistics && (
            <>
              <SimpleBarChart
                data={statistics.statusCounts}
                title="Complaints by Status"
              />
              <SimpleBarChart
                data={statistics.priorityCounts}
                title="Complaints by Priority"
              />
            </>
          )}
        </div>

        {/* Resolution Time Details */}
        {statistics && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Resolution Time Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {statistics.resolutionStats.min_hours.toFixed(1)}h
                </div>
                <div className="text-sm text-gray-600">Fastest Resolution</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {statistics.resolutionStats.avg_hours.toFixed(1)}h
                </div>
                <div className="text-sm text-gray-600">Average Resolution</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {statistics.resolutionStats.max_hours.toFixed(1)}h
                </div>
                <div className="text-sm text-gray-600">Slowest Resolution</div>
              </div>
            </div>
          </div>
        )}

        {/* Escalated Complaints */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Escalated Complaints</h3>
          </div>
          <div className="p-6">
            {escalatedComplaints.length > 0 ? (
              <div className="space-y-4">
                {escalatedComplaints.map((complaint) => (
                  <div key={complaint.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{complaint.title}</h4>
                      <p className="text-sm text-gray-600">#{complaint.complaint_number}</p>
                      <p className="text-sm text-gray-500">
                        By: {complaint.first_name} {complaint.last_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        ESCALATED
                      </span>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(complaint.escalated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No escalated complaints found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsDashboard
